/*
 * Компонент AdminUsers — управление пользователями
 *
 * Основные функции:
 * - Поиск пользователей по ID, имени, Telegram ID с debounce
 * - Просмотр и редактирование данных пользователя (имя, блокировка, роль админа)
 * - Ручное начисление дней подписки пользователю через CreateSimpleTransaction
 */
<script setup lang="ts">
// Реактивность, хуки жизненного цикла и наблюдатели Vue
import { onMounted, onUnmounted, ref, watch } from 'vue'
// Определение размера экрана — для динамического padding
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
// Стор администрирования — поиск и обновление пользователей, ручные транзакции
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Стор API — список тарифов для выбора при создании подписки
import { useApiStore } from '@/modules/api/store/apiStore'
// Утилита Pinia для реактивных ссылок на стейт стора
import { storeToRefs } from 'pinia'
// Компонент заголовка секции
import SectionHeader from '@/components/SectionHeader.vue'
// Типизация найденного пользователя
import type { ISearchedUser } from '@/modules/admin/types/ISearchedUser'
// Типизация payload обновления пользователя
import type { IUpdateUserPayload } from '@/modules/admin/types/UpdateUserPayload'
// Типизация proto Timestamp для форматирования дат
import type { ITimestampLike } from '@/modules/admin/types/TimestampLike'
// Переиспользуемый список транзакций с фильтрацией и диалогом отмены
import TransactionList from '@/modules/admin/components/TransactionList.vue'
// Composable фильтрации транзакций — контекст конкретного пользователя
import { useTransactionFilter } from '@/modules/admin/utils/useTransactionFilter'

// Инициализация стора администрирования
const adminStore = useAdminStore()
// Инициализация стора API (тарифы для формы подписки)
const apiStore = useApiStore()
// Определение мобильного экрана для динамического padding
const { mobile } = useDisplay()
// Реактивные ссылки на список пользователей и флаг поиска из стора
const { users, isSearched } = storeToRefs(adminStore)

// Загрузка списка тарифов при монтировании компонента
onMounted(async () => {
  await apiStore.getTariffs()
})

// Очистка стейта стора при размонтировании компонента
onUnmounted(() => {
  adminStore.users = []
  adminStore.transactions = []
})

// Строка поиска пользователей с debounce
const searchString = ref('')
// Карта оригинальных данных пользователей для отмены редактирования
const originalUsers = ref<Map<string, ISearchedUser>>(new Map())
// Таймер debounce для отложенного поиска
let searchTimeout: ReturnType<typeof setTimeout>

watch(searchString, (newValue) => {
  clearTimeout(searchTimeout)
  // clearable ставит null — приводим к пустой строке
  const value = newValue?.trim() ?? ''
  if (!value) {
    adminStore.users = []
    return
  }

  searchTimeout = setTimeout(() => {
    adminStore.getUsers(value)
  }, 500)
})

// Набор идентификаторов пользователей, находящихся в режиме редактирования
const editingUserIds = ref<Set<string>>(new Set())

/**
 * Переключает режим редактирования карточки пользователя
 *
 * Основные действия:
 * - Если пользователь уже редактируется — выходит из режима, удаляет оригинал
 * - Если нет — сохраняет копию оригинальных данных и включает режим редактирования
 *
 * @param {ISearchedUser} user - Объект пользователя для переключения режима
 */
const toggleEditMode = (user: ISearchedUser) => {
  if (editingUserIds.value.has(user.id)) {
    editingUserIds.value.delete(user.id)
    originalUsers.value.delete(user.id)
  } else {
    originalUsers.value.set(user.id, JSON.parse(JSON.stringify(user)))
    editingUserIds.value.add(user.id)
  }
}

/**
 * Отменяет редактирование и восстанавливает оригинальные данные пользователя
 *
 * @param {ISearchedUser} user - Объект пользователя для отмены изменений
 */
const cancelEdit = (user: ISearchedUser) => {
  const original = originalUsers.value.get(user.id)
  if (!original) return

  Object.assign(user, original)

  editingUserIds.value.delete(user.id)
  originalUsers.value.delete(user.id)
}

// Проверяет, находится ли пользователь в режиме редактирования
const isEditing = (id: string) => editingUserIds.value.has(id)

/**
 * Сохраняет изменения данных пользователя на сервер
 *
 * Основные действия:
 * - Формирует payload из текущих данных карточки
 * - Вызывает adminStore.updateUser для отправки через gRPC
 * - При успехе выходит из режима редактирования
 *
 * @param {ISearchedUser} user - Объект пользователя с изменёнными данными
 */
const saveUserChanges = async (user: ISearchedUser) => {
  const payload: IUpdateUserPayload = {
    id: user.id,
    firstName: user.firstName,
    email: user.email,
    referrerID: user.referrerID,
    description: user.description,
    isBlocked: user.isBlocked,
    isAdmin: user.isAdmin,
    isWebInterfaceOpened: user.isWebInterfaceOpened,
    telegramID: user.telegramID,
  }

  const success = await adminStore.updateUser(payload)
  if (success) {
    editingUserIds.value.delete(user.id)
    originalUsers.value.delete(user.id)
  }
}

// Флаг открытия диалога подтверждения удаления пользователя
const isDeleteDialogOpen = ref(false)
// Идентификатор пользователя, выбранного для удаления
const userIdToDelete = ref<string | null>(null)

/**
 * Открывает диалог подтверждения удаления пользователя
 *
 * @param {string} id - ID пользователя для удаления
 */
const openDeleteDialog = (id: string) => {
  userIdToDelete.value = id
  isDeleteDialogOpen.value = true
}

/**
 * Подтверждает удаление пользователя и вызывает action стора
 * После успешного удаления закрывает диалог и сбрасывает ID
 */
const confirmDeleteUser = async () => {
  if (!userIdToDelete.value) return
  await adminStore.deleteUser(userIdToDelete.value)
  userIdToDelete.value = null
  isDeleteDialogOpen.value = false
}

// Идентификатор пользователя, для которого открыта форма добавления дней (null если закрыта)
const simpleDaysUserId = ref<string | null>(null)
// Количество дней для добавления к подписке
const simpleDaysCount = ref<number | undefined>(undefined)

/**
 * Возвращает название тарифа по его идентификатору
 *
 * @param {string | undefined} tariffId - Идентификатор тарифа
 * @returns {string} Название тарифа (periodName) или прочерк
 */
const getTariffName = (tariffId?: string): string => {
  if (!tariffId) return '-'
  const tariff = apiStore.tariffs.find((t) => t.id === tariffId)
  return tariff ? tariff.periodName : tariffId
}

/**
 * Переключает форму добавления дней для пользователя
 *
 * Основные действия:
 * - Если форма уже открыта для этого пользователя — закрывает её
 * - Если нет — открывает форму и сбрасывает количество дней
 *
 * @param {string} userId - Идентификатор пользователя
 */
const toggleSimpleDaysForm = (userId: string) => {
  if (simpleDaysUserId.value === userId) {
    simpleDaysUserId.value = null
    return
  }

  simpleDaysUserId.value = userId
  simpleDaysCount.value = undefined
}

/**
 * Создаёт простую транзакцию для добавления дней к подписке
 *
 * Основные действия:
 * - Валидирует что количество дней задано и больше 0
 * - Вызывает adminStore.createSimpleTransaction для отправки через gRPC
 * - При успехе закрывает форму
 *
 * @param {string} userId - Идентификатор пользователя
 */
const createSimpleTransaction = async (userId: string) => {
  if (!simpleDaysCount.value || simpleDaysCount.value <= 0) return

  const success = await adminStore.createSimpleTransaction({
    userID: userId,
    durationDays: simpleDaysCount.value,
  })

  if (success) {
    simpleDaysUserId.value = null
    // Перезагружаем данные пользователей для актуализации подписки (lastTariffExpiresAt, isTariffActive)
    if (searchString.value?.trim()) {
      await adminStore.getUsers(searchString.value)
    }
  }
}

/**
 * Форматирует proto Timestamp в читаемую дату в русской локали
 *
 * @param {ITimestampLike | undefined} ts - Объект Timestamp с полем seconds
 * @returns {string} Отформатированная дата или '-' если timestamp отсутствует
 */
const formatTimestamp = (ts: ITimestampLike | undefined) => {
  if (!ts || !ts.seconds) return '-'
  const date = new Date(Number(ts.seconds) * 1000)
  return date.toLocaleString('ru-RU')
}

/**
 * Форматирует дату окончания подписки с количеством оставшихся дней
 *
 * @param {number | null} expiresAt - Unix timestamp в секундах или null
 * @returns {string} Дата + оставшиеся дни в скобках, или прочерк
 */
const formatExpiresAt = (expiresAt: number | null): string => {
  if (!expiresAt) return '-'
  const date = new Date(expiresAt * 1000)
  const formatted = date.toLocaleString('ru-RU')
  const now = new Date()
  const diffMs = date.getTime() - now.getTime()
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return `${formatted} (${diffDays} дн.)`
}

// Идентификатор пользователя, для которого открыта секция транзакций (null если закрыта)
const transactionsUserId = ref<string | null>(null)

// Composable фильтрации транзакций с привязкой к выбранному пользователю
const {
  filterID: txFilterID,
  loadTransactions,
  onStatusChanged,
} = useTransactionFilter({
  fixedUserID: () => transactionsUserId.value,
})

/**
 * Переключает отображение транзакций для пользователя
 *
 * Основные действия:
 * - Если секция уже открыта для этого пользователя — скрывает её
 * - Если нет — сбрасывает фильтр, загружает транзакции и показывает секцию
 *
 * @param {string} userId - Идентификатор пользователя
 */
const toggleUserTransactions = async (userId: string) => {
  if (transactionsUserId.value === userId) {
    transactionsUserId.value = null
    return
  }

  transactionsUserId.value = userId
  // Сбрасываем фильтр по ID транзакции при переключении пользователя
  txFilterID.value = ''
  await loadTransactions()
}

/**
 * Обработчик отмены транзакции — перезагружает транзакции и данные пользователей
 */
const onTransactionCancelled = async () => {
  // 1. Перезагружаем транзакции пользователя с текущими фильтрами
  await loadTransactions()
  // 2. Перезагружаем данные пользователей для актуализации подписки (lastTariffExpiresAt, isTariffActive)
  if (searchString.value?.trim()) {
    await adminStore.getUsers(searchString.value)
  }
}
</script>

<template>
  <div class="d-flex flex-column ga-6 w-100 pt-4" :class="{ 'pa-4': isTelegram() || mobile }" style="max-width: 100vw; box-sizing: border-box;">
    <header class="d-flex justify-space-between align-center">
      <section-header title="Управление пользователями" />
    </header>

    <section class="d-flex flex-column ga-2">
      <div class="position-relative">
        <v-text-field
          v-model="searchString"
          label="Поиск по ID, Имени, Telegram ID..."
          prepend-inner-icon="ri-search-line"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          class="mb-2"
        />
        <v-progress-linear
          v-if="isSearched"
          indeterminate
          color="primary"
          height="2"
          class="position-absolute bottom-0 w-100"
          style="bottom: 8px; left: 0; z-index: 1"
        />
      </div>

      <div
        v-if="!isSearched && (!users || users.length === 0)"
        class="text-center text-text-disabled pa-4"
      >
        Пользователи не найдены
      </div>

      <div v-for="user in users" :key="user.id" class="w-100">
        <v-card variant="flat" color="surface" class="pa-4 d-flex flex-column ga-2 w-100">
          <v-form @submit.prevent="saveUserChanges(user)" class="w-100">
            <div class="d-flex flex-column ga-2">
              <div class="d-flex flex-column ga-2" style="flex: 1">
                <v-textarea
                   v-model="user.id"
                   label="ID"
                   readonly
                   density="compact"
                   variant="outlined"
                   hide-details
                   rows="1"
                   auto-grow
                   no-resize
                 />
                <v-text-field
                  v-model.number="user.telegramID"
                  label="Telegram ID"
                  :readonly="!isEditing(user.id)"
                  type="number"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  v-model="user.referrerID"
                  label="Referrer ID"
                  :readonly="!isEditing(user.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
              </div>

              <div class="d-flex flex-column ga-2" style="flex: 1">
                <v-text-field
                  v-model="user.firstName"
                  label="Имя"
                  :readonly="!isEditing(user.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <!-- Email пользователя (устанавливается администратором напрямую) -->
                <v-text-field
                  v-model="user.email"
                  label="Email"
                  :readonly="!isEditing(user.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <!-- Неподтверждённый email — показывается только если пользователь начал привязку, но не верифицировал. Всегда readonly: подтверждает сам пользователь. -->
                <v-text-field
                  v-if="user.newEmail"
                  :model-value="user.newEmail"
                  label="Неподтверждённый email"
                  readonly
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-textarea
                  v-model="user.description"
                  label="Заметки"
                  :readonly="!isEditing(user.id)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  rows="1"
                  auto-grow
                />
              </div>

              <div class="d-flex flex-column ga-2" style="flex: 1">
                <v-text-field
                  :model-value="formatTimestamp(user.createdAt)"
                  label="Регистрация"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  :model-value="formatTimestamp(user.lastSeenAt)"
                  label="Был в сети"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <v-text-field
                  v-model="user.referralCount"
                  label="Пригласил"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                  prefix="users:"
                />
                <v-text-field
                  v-model="user.referralBonusDaysCount"
                  label="Получено бонусных дней"
                  readonly
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                 <v-textarea
                   :model-value="user.referralLink || '—'"
                   label="Реферальная ссылка"
                   readonly
                   density="compact"
                   variant="outlined"
                   hide-details
                   rows="1"
                   auto-grow
                   no-resize
                 />
                <v-text-field
                  :model-value="getTariffName(user.lastTariffID)"
                  label="Текущий тариф"
                  disabled
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <!-- Дата окончания подписки из lastTariffExpiresAt (unix timestamp секунд) -->
                <v-text-field
                  :model-value="formatExpiresAt(user.lastTariffExpiresAt)"
                  label="Подписка до"
                  readonly
                  density="compact"
                  variant="outlined"
                  hide-details
                />
                <div class="d-flex align-center pt-1">
                  <v-chip
                    :color="user.hasUsedPromo ? 'success' : 'info'"
                    variant="tonal"
                    size="small"
                    prepend-icon="ri-gift-line"
                  >
                    {{ user.hasUsedPromo ? 'Промо-тариф использован' : 'Промо-тариф не использован' }}
                  </v-chip>
                </div>
              </div>

              <div
                class="d-flex flex-column ga-2"
                style="flex: 1; min-width: 0"
              >
                <div v-if="isEditing(user.id)" class="d-flex w-100 justify-space-evenly align-center">
                  <v-switch
                    v-model="user.isBlocked"
                    color="error"
                    label="Блок"
                    hide-details
                    density="compact"
                  />
                  <v-switch
                    v-model="user.isAdmin"
                    color="primary"
                    label="Админ"
                    hide-details
                    density="compact"
                  />
                </div>

                <div class="d-flex flex-column ga-2 w-100">
                  <template v-if="isEditing(user.id)">
                    <v-btn
                      color="success"
                      variant="flat"
                      type="submit"
                      class="flex-grow-1"
                    >
                      Сохранить
                    </v-btn>
                    <v-btn
                      color="secondary"
                      variant="outlined"
                      @click="cancelEdit(user)"
                    >
                      Отмена
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      prepend-icon="ri-pencil-line"
                      class="flex-grow-1"
                      @click="toggleEditMode(user)"
                    >
                      Изменить
                    </v-btn>
                    <v-btn
                      color="error"
                      variant="outlined"
                      prepend-icon="ri-delete-bin-line"
                      class="flex-grow-1"
                      @click="openDeleteDialog(user.id)"
                    >
                      Удалить
                    </v-btn>
                  </template>
                </div>
              </div>
            </div>
          </v-form>

          <!-- Добавить дни к подписке -->
          <div class="d-flex flex-column ga-2">
            <v-btn
              color="secondary"
              variant="outlined"
              prepend-icon="ri-add-line"
              block
              @click="toggleSimpleDaysForm(user.id)"
            >
              Добавить дни
            </v-btn>

            <div
              v-if="simpleDaysUserId === user.id"
              class="d-flex flex-column ga-2"
            >
              <v-text-field
                v-model.number="simpleDaysCount"
                label="Количество дней"
                type="number"
                density="compact"
                variant="outlined"
                hide-details
                min="1"
              />

              <v-btn
                color="secondary"
                variant="flat"
                block
                @click="createSimpleTransaction(user.id)"
              >
                Добавить
              </v-btn>
            </div>
          </div>

          <!-- Транзакции пользователя -->
          <div class="d-flex flex-column ga-2">
            <v-btn
              color="info"
              variant="outlined"
              prepend-icon="ri-exchange-funds-line"
              block
              @click="toggleUserTransactions(user.id)"
            >
              {{ transactionsUserId === user.id ? 'Скрыть транзакции' : 'Транзакции' }}
            </v-btn>

            <template v-if="transactionsUserId === user.id">
              <!-- Фильтр по ID транзакции внутри карточки пользователя -->
              <v-text-field
                v-model="txFilterID"
                label="Фильтр по ID транзакции"
                variant="outlined"
                density="compact"
                hide-details
                clearable
              />

              <transaction-list
                hide-user-i-d
                @status-changed="onStatusChanged"
                @cancelled="onTransactionCancelled"
              />
            </template>
          </div>
        </v-card>
      </div>
    </section>

    <!-- Диалог подтверждения удаления пользователя -->
    <v-dialog v-model="isDeleteDialogOpen" max-width="420">
      <v-card class="d-flex flex-column ga-4 pa-4">
        <div class="text-h6">Удаление пользователя</div>

        <div class="text-body-1">
          Вы уверены, что хотите удалить этого пользователя? Это действие необратимо.
        </div>

        <div class="d-flex justify-end ga-2">
          <v-btn variant="text" @click="isDeleteDialogOpen = false"> Отмена </v-btn>

          <v-btn color="error" variant="flat" @click="confirmDeleteUser"> Удалить </v-btn>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<style scoped>
:deep(.v-textarea .v-field__input),
:deep(.v-textarea__sizer) {
  word-break: break-all !important;
}
</style>
